import {
    useCallback, useEffect, useRef, useState,
} from "react";
import axiosManager from "../services/axiosManager";

const POLL_INTERVAL_MS = 10_000;
const POLL_TIMEOUT_MS = 40_000;

export const useGetContractPolling = ({
    studentId, hasSeveralSignees, onComplete, onError,
}) => {
    const [isSigned, setIsSigned] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const [contractError, setContractError] = useState(null);
    const [signees, setSignees] = useState(null);

    const onCompleteRef = useRef(onComplete);
    const onErrorRef = useRef(onError);

    useEffect(() => {
        onCompleteRef.current = onComplete;
        onErrorRef.current = onError;
    }, [onComplete, onError]);

    const intervalRef = useRef(null);
    const timeoutRef = useRef(null);

    const stop = () => {
        clearInterval(intervalRef.current);
        clearTimeout(timeoutRef.current);
        setIsPolling(false);
    };

    const pollSingle = async () => {
        try {
            const response = await axiosManager.get$(`/parent/api/students/${studentId}/contracts`);
            const status = response?.data?.status;
            const updatedSignees = response?.data?.signees;

            setSignees(updatedSignees);

            if (status === "Complete") {
                stop();
                setIsSigned(true);
                onCompleteRef.current?.();
            } else if (status !== "Sent") {
                const error = { reason: "unexpected_status", status };
                stop();
                setContractError(error);
                onErrorRef.current?.(error);
            }
        } catch (err) {
            stop();
            setContractError(err);
            onErrorRef.current?.(err);
        }
    };

    const pollSignees = async () => {
        try {
            const response = await axiosManager.get$(`/parent/api/students/${studentId}/contracts`);
            const updatedSignees = response?.data?.signees;

            setSignees(updatedSignees);

            const atLeastOneSigned = updatedSignees?.some((s) => s.signingStatus === "Signed");
            if (atLeastOneSigned) {
                stop();
                onCompleteRef.current?.();
            }
        } catch (err) {
            stop();
            setContractError(err);
            onErrorRef.current?.(err);
        }
    };

    const startPolling = useCallback(() => {
        if (!studentId) return;

        const poll = hasSeveralSignees ? pollSignees : pollSingle;

        setContractError(null);
        setIsPolling(true);

        poll();
        intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

        if (!hasSeveralSignees) {
            timeoutRef.current = setTimeout(() => {
                const error = { reason: "timeout" };
                stop();
                setContractError(error);
                onErrorRef.current?.(error);
            }, POLL_TIMEOUT_MS);
        }
    }, [studentId, hasSeveralSignees]);

    useEffect(() => stop, []);

    return {
        isSigned,
        isPolling,
        contractError,
        signees,
        startPolling,
    };
};
